import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, MessageCircle, Share2, MapPin, Star } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import LikeButton from "./LikeButton";
import SaveButton from "./SaveButton";
import AdminControls from "../admin/AdminControls";

const BADGE_COLORS = {
  admin: "bg-red-100 text-red-700",
  local_expert: "bg-[#F59E0B]/10 text-[#D97706]",
  active_reviewer: "bg-[#3FAF7A]/10 text-[#3FAF7A]",
  creator: "bg-[#3B82F6]/10 text-[#3B82F6]",
  experience: "bg-[#3B82F6]/10 text-[#3B82F6]",
  new_traveler: "bg-[#E2E8E4] text-[#5C6B64]",
};

export default function PostCard({ post, onVote, onShare }) {
  const { user } = useAuth();
  return (
    <div
      className="bg-white border border-[#E2E8E4] rounded-2xl p-4 sm:p-5 flex gap-3 sm:gap-4 transition-all hover:border-[#3FAF7A]/50 hover:shadow-sm overflow-hidden"
      data-testid={`post-card-${post.id}`}
    >
      {/* Vote column */}
      <div className="flex flex-col items-center gap-0.5 min-w-[32px] sm:min-w-[40px] flex-shrink-0">
        <button
          onClick={() => onVote?.(post.id, 1)}
          className={`p-1 rounded hover:bg-[#3FAF7A]/10 transition-colors ${
            post.user_vote === 1 ? "text-[#3FAF7A]" : "text-[#5C6B64]"
          }`}
          data-testid={`upvote-${post.id}`}
        >
          <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
        </button>
        <span className="text-xs sm:text-sm font-bold text-[#0B1B13]">{post.vote_score || 0}</span>
        <button
          onClick={() => onVote?.(post.id, -1)}
          className={`p-1 rounded hover:bg-red-50 transition-colors ${
            post.user_vote === -1 ? "text-red-500" : "text-[#5C6B64]"
          }`}
          data-testid={`downvote-${post.id}`}
        >
          <ArrowDown className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Content column — min-w-0 is CRITICAL for overflow to work */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <Link to={`/posts/${post.id}`} className="block">
          {/* Meta row */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {post.rating && (
              <span className="flex items-center gap-0.5 text-xs text-[#F59E0B] flex-shrink-0">
                <Star className="w-3 h-3 fill-current" strokeWidth={1.5} /> {post.rating}
              </span>
            )}
            {post.place && (
              <span className="flex items-center gap-0.5 text-xs text-[#5C6B64] min-w-0 overflow-hidden">
                <MapPin className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
                <span className="truncate">{post.place.name}</span>
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="font-medium text-[#0B1B13] text-sm sm:text-base hover:text-[#123D2B] transition-colors mb-1 line-clamp-2 break-words">
            {post.title}
          </h3>

          {/* Content preview */}
          <p className="text-xs sm:text-sm text-[#5C6B64] line-clamp-2 mb-3 break-words">
            {post.content}
          </p>

          {/* Image */}
          {(post.media_urls?.[0] || post.images?.[0] || post.image || post.photo_url) && (
            <img
              src={post.media_urls?.[0] || post.images?.[0] || post.image || post.photo_url}
              className="w-full h-36 sm:h-48 object-cover rounded-xl border border-[#E2E8E4] mb-3"
              alt={post.title}
              onError={(e) => {
                if (e.target.src !== "/fallback.jpg") {
                  e.target.src = "/fallback.jpg";
                } else {
                  e.target.style.display = "none";
                }
              }}
            />
          )}
        </Link>

        {/* Footer row */}
        <div className="flex items-center gap-2 sm:gap-4 text-xs text-[#5C6B64] flex-wrap min-w-0">
          {/* Author */}
          <span className="flex items-center gap-1 flex-wrap min-w-0 overflow-hidden">
            <div className="w-5 h-5 rounded-full bg-[#3FAF7A]/10 flex items-center justify-center text-[#3FAF7A] text-[10px] font-bold flex-shrink-0">
              {post.author?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <span className="truncate max-w-[80px] sm:max-w-none">
              {post.author?.name || "Unknown"}
            </span>

            {post.author?.badges?.filter((b) => b !== "new_traveler").map((b) => (
              <span
                key={b}
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide flex-shrink-0 ${
                  BADGE_COLORS[b] || BADGE_COLORS.new_traveler
                }`}
              >
                {b.replace(/_/g, " ")}
              </span>
            ))}
          </span>

          {/* Comments */}
          <Link to={`/posts/${post.id}`} className="flex items-center gap-1 hover:text-[#123D2B] flex-shrink-0">
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
            {post.comments_count || post.comment_count || 0}
          </Link>

          <LikeButton post={post} user={user} />
          <SaveButton post={post} user={user} />

          <button
            onClick={() => onShare?.(post.id)}
            className="flex items-center gap-1 hover:text-[#3FAF7A] flex-shrink-0"
            data-testid={`share-${post.id}`}
          >
            <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Share
          </button>

          <span className="flex-shrink-0">
            {new Date(post.created_at).toLocaleDateString()}
          </span>

          <AdminControls post={post} user={user} />
        </div>
      </div>
    </div>
  );
}